/**
 * Addresses inside the app. Only IDs and fixed words go in the address — never tokens, emails, names,
 * recipients, file choices, search text or filters — so a copied link reveals nothing by itself; opening
 * it checks access like any read, and opening or refreshing one never changes anything.
 *
 *   /                                        workspace chooser
 *   /w/<ws>                                  the workspace (Dispatch)
 *   /w/<ws>/<section>                        a section's main view (today, companies, send-history …)
 *   /w/<ws>/orders/<order>[/<tab>]           one order; tab: documents, messages, activity
 *   /w/<ws>/orders/<order>/duplicates        its possible-duplicate review
 *   /w/<ws>/orders/<order>/send              its email draft (the draft stays on this device, not in the address)
 *   /w/<ws>/orders/<order>/changes/<request> the review of one open schedule change request
 *   /w/<ws>/duplicates                       the possible-duplicates list
 *   /w/<ws>/bulk/<selection>                 a saved bulk send job (draft, preparing, review or sending)
 *   /w/<ws>/sends/<send>                     one send that was submitted
 *   /w/<ws>/companies/<company>[/<tab>]      one company; tab: contacts, rules, portal, issues, activity (playbook is the company)
 *   /w/<ws>/companies/<company>/rules/<rule> one saved extraction rule (a new draft stays on /rules, unsaved)
 *   /w/<ws>/runs[/<run>]                     AI & MCP tasks, or one task
 *   /w/<ws>/documents/<document>             one document in the library
 *   /w/<ws>/documents/<tab>                  a Documents list: analyses, uploads (files is /documents)
 *   /w/<ws>/analyses/<analysis>              one document analysis review
 *   /w/<ws>/invoices/<invoice>               one invoice (read access checked by its view)
 *   /w/<ws>/routes/<route>                   one private route (all-order access rechecked)
 *   /w/<ws>/upload-batches/<batch>           one upload batch receipt (in Activity)
 *   /w/<ws>/activity/export                  the audit export page
 *   /w/<ws>/settings/<tab>                   a Settings tab: notifications, ai, apps (appearance is /settings)
 *   /w/<ws>/team/<tab>                       a Team tab: access, invitations (members is /team)
 *   /w/<ws>/connections/<tab>                a Connections tab: ai, routing, usage, policy (accounts is /connections)
 *   /w/<ws>/activity/<tab>                   an Activity tab: operations, conflicts (history is /activity; tasks are /runs)
 *   /w/<ws>/workflows/<workflow>[/history]   one saved workflow, or its run history (a preview is computed on demand, not addressed)
 *   /w/<ws>/listings/<occurrence>            one auction listing in Vehicle lookup (re-read with current access; the VIN never appears in the address)
 *
 * Anything else is "not a place in the app" and opens the workspace's start instead.
 */
export const SECTIONS = ['today', 'companies', 'dispatch', 'routes', 'documents', 'invoices', 'sendHistory', 'trucks', 'connections', 'settings',
  'activity', 'workflows', 'lookup', 'billing', 'team', 'shipments'] as const;
export type RouteSection = typeof SECTIONS[number];
export const ORDER_VIEWS = ['documents', 'messages', 'activity', 'duplicates', 'send'] as const;
export type OrderView = typeof ORDER_VIEWS[number];
export const COMPANY_TABS = ['contacts', 'rules', 'portal', 'issues', 'activity'] as const;
export type CompanyTab = typeof COMPANY_TABS[number];
export type RouteRecord =
  | { kind: 'order'; id: string; view?: OrderView; change?: string }
  | { kind: 'company'; id: string; tab?: CompanyTab; rule?: string }
  | { kind: 'workflow'; id: string; tab?: 'history' }
  | { kind: 'run' | 'document' | 'bulk' | 'send' | 'invoice' | 'shipment' | 'shipmentInvitation' | 'route' | 'batch' | 'analysis' | 'listing'; id: string };
/** A section's own sub-view without a record: the duplicates list (dispatch), the task list and the export
 * page (activity), and the non-default Settings and Team tabs. */
export type SectionView = 'duplicates' | 'runs' | 'export' | 'notifications' | 'ai' | 'apps' | 'data' | 'security' | 'closing' | 'news' | 'access' | 'invitations' | 'analyses' | 'uploads'
  | 'connAi' | 'routing' | 'usage' | 'policy' | 'operations' | 'conflicts';
export interface Route { workspaceId: string | null; section: RouteSection | null; record: RouteRecord | null; view?: SectionView | null }
/** Where a section is: one record, one of its views, or (null) its main list. */
export type Place = { record: RouteRecord } | { view: SectionView } | null;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const slug = (s: RouteSection) => s.replace(/[A-Z]/g, c => '-' + c.toLowerCase());
const BY_SLUG = new Map(SECTIONS.map(s => [slug(s), s]));
const RECORDS: Record<RouteRecord['kind'], { slug: string; section: RouteSection }> = {
  order: { slug: 'orders', section: 'dispatch' }, company: { slug: 'companies', section: 'companies' }, run: { slug: 'runs', section: 'activity' },
  document: { slug: 'documents', section: 'documents' }, bulk: { slug: 'bulk', section: 'dispatch' }, send: { slug: 'sends', section: 'sendHistory' },
  invoice: { slug: 'invoices', section: 'invoices' },
  shipment: {slug:'shipments',section:'shipments'},
  shipmentInvitation: {slug:'shipment-invitations',section:'shipments'},
  route: {slug:'routes',section:'routes'},
  batch: {slug:'upload-batches',section:'activity'},
  analysis: {slug:'analyses',section:'documents'},
  workflow: {slug:'workflows',section:'workflows'},
  listing: {slug:'listings',section:'lookup'},
};
const BY_RECORD_SLUG = new Map(Object.entries(RECORDS).map(([kind, r]) => [r.slug, kind as RouteRecord['kind']]));
const VIEWS: Record<SectionView, { section: RouteSection; path: string }> = {
  duplicates: { section: 'dispatch', path: 'duplicates' }, runs: { section: 'activity', path: 'runs' }, export: { section: 'activity', path: 'activity/export' },
  notifications: { section: 'settings', path: 'settings/notifications' }, ai: { section: 'settings', path: 'settings/ai' }, apps: { section: 'settings', path: 'settings/apps' }, data: { section: 'settings', path: 'settings/data' }, security: { section: 'settings', path: 'settings/security' }, closing: { section: 'settings', path: 'settings/workspace' }, news: { section: 'settings', path: 'settings/whats-new' },
  access: { section: 'team', path: 'team/access' }, invitations: { section: 'team', path: 'team/invitations' },
  analyses: { section: 'documents', path: 'documents/analyses' }, uploads: { section: 'documents', path: 'documents/uploads' },
  connAi: { section: 'connections', path: 'connections/ai' }, routing: { section: 'connections', path: 'connections/routing' },
  usage: { section: 'connections', path: 'connections/usage' }, policy: { section: 'connections', path: 'connections/policy' },
  operations: { section: 'activity', path: 'activity/operations' }, conflicts: { section: 'activity', path: 'activity/conflicts' },
};
const BY_VIEW_PATH = new Map(Object.entries(VIEWS).map(([view, v]) => [v.path, view as SectionView]));
/** The views a section has (for checking a place reported or requested for it). */
export const viewsOf = (section: RouteSection) => (Object.keys(VIEWS) as SectionView[]).filter(v => VIEWS[v].section === section);

export const EMPTY_ROUTE: Route = { workspaceId: null, section: null, record: null };

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'w' || !parts[1] || !UUID.test(parts[1])) return EMPTY_ROUTE;
  const workspaceId = parts[1].toLowerCase();
  const start: Route = { workspaceId, section: null, record: null };
  if (parts.length === 2) return start;
  const [, , a, b, c, d] = parts as [string, string, string, string?, string?, string?];
  if (a === 'orders' && b && UUID.test(b) && c === 'changes' && d && UUID.test(d) && parts.length === 6)
    return { workspaceId, section: 'dispatch', record: { kind: 'order', id: b.toLowerCase(), change: d.toLowerCase() } };
  if (a === 'companies' && b && UUID.test(b)) {
    const id = b.toLowerCase();
    if (parts.length === 4) return { workspaceId, section: 'companies', record: { kind: 'company', id } };
    if (parts.length === 5 && (COMPANY_TABS as readonly string[]).includes(c!)) return { workspaceId, section: 'companies', record: { kind: 'company', id, tab: c as CompanyTab } };
    if (parts.length === 6 && c === 'rules' && d && UUID.test(d)) return { workspaceId, section: 'companies', record: { kind: 'company', id, tab: 'rules', rule: d.toLowerCase() } };
    return start;
  }
  if (a === 'workflows' && b && UUID.test(b)) {
    const id = b.toLowerCase();
    if (parts.length === 4) return { workspaceId, section: 'workflows', record: { kind: 'workflow', id } };
    if (parts.length === 5 && c === 'history') return { workspaceId, section: 'workflows', record: { kind: 'workflow', id, tab: 'history' } };
    return start;
  }
  if (b && UUID.test(b) && parts.length <= 5) {
    const kind = BY_RECORD_SLUG.get(a);
    if (kind === 'order') {
      if (c && !(ORDER_VIEWS as readonly string[]).includes(c)) return start;
      return { workspaceId, section: 'dispatch', record: { kind, id: b.toLowerCase(), ...(c ? { view: c as OrderView } : {}) } };
    }
    if (kind && !c) return { workspaceId, section: RECORDS[kind].section, record: { kind, id: b.toLowerCase() } };
    return start;
  }
  const view = parts.length <= 4 ? BY_VIEW_PATH.get(parts.slice(2).join('/')) : undefined;
  if (view) return { workspaceId, section: VIEWS[view].section, record: null, view };
  if (parts.length === 3) {
    const section = BY_SLUG.get(a);
    if (section) return { workspaceId, section, record: null };
  }
  return start;
}

export function formatRoute(r: Route): string {
  if (!r.workspaceId) return '/';
  const w = `/w/${r.workspaceId}`;
  if (r.record) {
    const base = `${w}/${RECORDS[r.record.kind].slug}/${r.record.id}`;
    if (r.record.kind === 'order' && r.record.change) return `${base}/changes/${r.record.change}`;
    if (r.record.kind === 'workflow') return r.record.tab ? `${base}/${r.record.tab}` : base;
    if (r.record.kind === 'company') return r.record.rule ? `${base}/rules/${r.record.rule}` : r.record.tab ? `${base}/${r.record.tab}` : base;
    return r.record.kind === 'order' && r.record.view ? `${base}/${r.record.view}` : base;
  }
  if (r.view) return `${w}/${VIEWS[r.view].path}`;
  return r.section && r.section !== 'dispatch' ? `${w}/${slug(r.section)}` : w;
}

/** The route for a section showing `place`. */
export function routeAt(workspaceId: string, section: RouteSection, place: Place): Route {
  if (place && 'record' in place) return { workspaceId, section, record: place.record };
  if (place && 'view' in place) return { workspaceId, section, record: null, view: place.view };
  return { workspaceId, section, record: null };
}
/** The place a route names inside its section. */
export function placeOf(r: Route): Place {
  return r.record ? { record: r.record } : r.view ? { view: r.view } : null;
}
export const placeKey = (p: Place) => p === null ? '' : 'record' in p ? [p.record.kind, p.record.id, 'view' in p.record ? p.record.view ?? '' : '', 'change' in p.record ? p.record.change ?? '' : '',
  'tab' in p.record ? p.record.tab ?? '' : '', 'rule' in p.record ? p.record.rule ?? '' : ''].join(':') : `view:${p.view}`;

export const sameRoute = (a: Route, b: Route) => formatRoute(a) === formatRoute(b);
export const currentRoute = () => parseRoute(window.location.pathname);

/** Adds a history entry for `r` unless the address already shows it. */
export function pushRoute(r: Route) {
  const path = formatRoute(r);
  if (window.location.pathname !== path) window.history.pushState(null, '', path + window.location.search);
}
/** Replaces the current entry (no new Back step), e.g. when the address was not a place in the app. */
export function replaceRoute(r: Route) {
  const path = formatRoute(r);
  if (window.location.pathname !== path) window.history.replaceState(null, '', path + window.location.search);
}
